import EmptyState from '@/components/EmptyState';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import { useLoyaltyConfig, useUpsertLoyaltyConfig } from '@/features/loyalty/hooks/useLoyalty';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { inputToMinor, moneyToInput } from '@/utils/money';
import { bpsToPercent } from '@/utils/percent';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useMemo, useState } from 'react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

function LoyaltySettingsPage() {
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: config, isLoading, error } = useLoyaltyConfig();
  const upsert = useUpsertLoyaltyConfig();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';

  const [enabled, setEnabled] = useState(false);
  // Operator language: points earned per 1 currency unit (earnRateBps = ×100).
  const [pointsPerUnit, setPointsPerUnit] = useState('1');
  // Money one point is worth on redemption.
  const [redeemValue, setRedeemValue] = useState('');

  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled);
    setPointsPerUnit(String(bpsToPercent(config.earnRateBps)));
    setRedeemValue(
      config.redeemValueCents > 0 ? moneyToInput(config.redeemValueCents, currency) : '',
    );
  }, [config, currency]);

  // Dirty = live fields differ from the saved config. After a save the query
  // refreshes `config`, so `pristine` catches up and the dirty flag clears.
  const pristine = useMemo(
    () => ({
      enabled: config?.enabled ?? false,
      pointsPerUnit: config ? String(bpsToPercent(config.earnRateBps)) : '1',
      redeemValue:
        config && config.redeemValueCents > 0
          ? moneyToInput(config.redeemValueCents, currency)
          : '',
    }),
    [config, currency],
  );
  const isDirty =
    !!config &&
    JSON.stringify({ enabled, pointsPerUnit, redeemValue }) !== JSON.stringify(pristine);

  const save = () => {
    upsert.mutate(
      {
        enabled,
        earnRateBps: Math.round(parseFloat(pointsPerUnit || '0') * 100),
        redeemValueCents: redeemValue.trim() ? inputToMinor(redeemValue, currency) : 0,
      },
      {
        onSuccess: () =>
          f7.toast.show({ text: 'Loyalty settings saved', closeTimeout: 2000, position: 'bottom' }),
      },
    );
  };

  return (
    <Page name="loyalty-settings">
      <Navbar title="Loyalty" backLink={settingsBackLink}>
        <SettingsSaveNav isDirty={isDirty} saving={upsert.isPending} onSave={save} />
      </Navbar>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title="Couldn’t load loyalty settings"
          description={(error as Error).message}
        />
      )}

      {config && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title="Loyalty program">
              <ToggleSwitch slot="after" checked={enabled} onChange={setEnabled} />
            </ListItem>
          </List>

          {enabled && (
            <>
              <BlockTitle>Earning</BlockTitle>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                <ListInput
                  label={`Points earned per ${currency} spent`}
                  type="number"
                  min="0"
                  step="0.1"
                  value={pointsPerUnit}
                  onInput={(e) => setPointsPerUnit(e.target.value)}
                >
                  <span slot="info">e.g. 1 point for every {currency} on the item subtotal</span>
                </ListInput>
              </List>

              <BlockTitle>Redemption</BlockTitle>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                <ListInput
                  label={`What 1 point is worth (${currency} off)`}
                  type="text"
                  placeholder="0.10"
                  value={redeemValue}
                  onInput={(e) => setRedeemValue(e.target.value)}
                >
                  <span slot="info">Guests redeem points for money off at checkout</span>
                </ListInput>
              </List>
            </>
          )}

          <BlockFooter>
            Points are earned on the item subtotal of a completed order or dine-in visit (excludes
            delivery, tax, and tips). Redemption applies as money off the next order.
          </BlockFooter>
        </>
      )}
    </Page>
  );
}

export default LoyaltySettingsPage;
